import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { ChangeEvent } from 'preact/compat';
import { Theme } from '@app/theme';
import {
  CountryKeySelect,
  Input,
  OptionsObject,
  TextAndImageSelectionField,
} from '@app/form';
import { useOrganization, useOrganizationMembers } from 'queries';
import { OrganizationMemberSelect } from 'components/OrganizationMemberSelect';
import { SelectionRow } from './SelectionRow';

export interface Recipient {
  fromOrganization: boolean;
  organizationId: string;
  clientId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  billingCountryCode?: string;
}

export const initialRecipient: Recipient = {
  fromOrganization: true,
  organizationId: '',
  clientId: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  companyName: '',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What the id typed in the organization field currently points at, if anything. */
export type OrganizationLookup =
  | 'empty'
  | 'invalidId'
  | 'loading'
  | 'notFound'
  | 'withoutOwner'
  | 'found';

export const useQuoteRecipient = (recipient: Recipient) => {
  const organizationId =
    recipient.fromOrganization && UUID.test(recipient.organizationId)
      ? recipient.organizationId
      : undefined;

  const {
    data: organization,
    isLoading: organizationLoading,
    isError: organizationNotFound,
  } = useOrganization(organizationId, { retry: false });
  const {
    data: members,
    isLoading: membersLoading,
    isError: membersNotFound,
  } = useOrganizationMembers(organizationId, { retry: false });

  const owner = useMemo(
    () =>
      members?.find(
        (member) =>
          member.id === organization?.ownerId ||
          member.accountId === organization?.ownerId,
      ),
    [members, organization],
  );

  const lookup: OrganizationLookup = useMemo(() => {
    if (!recipient.organizationId) {
      return 'empty';
    }
    if (!organizationId) {
      return 'invalidId';
    }
    if (organizationNotFound || membersNotFound) {
      return 'notFound';
    }
    if (organizationLoading || membersLoading) {
      return 'loading';
    }
    if (!organization) {
      return 'notFound';
    }
    return owner ? 'found' : 'withoutOwner';
  }, [
    recipient.organizationId,
    organizationId,
    organization,
    organizationLoading,
    organizationNotFound,
    membersLoading,
    membersNotFound,
    owner,
  ]);

  const chosen = recipient.fromOrganization
    ? !!owner
    : !!recipient.firstName &&
      !!recipient.lastName &&
      !!recipient.email &&
      !!recipient.companyName &&
      !!recipient.billingCountryCode;

  return { organization, organizationId, members, owner, lookup, chosen };
};

const useStyles = createUseStyles((theme: Theme) => ({
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.marginBase * 1.5,

    [theme.media.tablet]: {
      gridTemplateColumns: '1fr',
    },
  },
  wide: {
    gridColumn: '1 / -1',
  },
  found: {
    ...theme.fonts.caption,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    color: theme.colors.greenAccessible,
  },
  notFound: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  lookupError: {
    ...theme.fonts.caption,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    color: theme.colors.redAccessible,
  },
}));

interface Props {
  value: Recipient;
  onChange: (recipient: Recipient) => void;
}

export const RecipientSection = ({ value, onChange }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { organization, organizationId, members, owner, lookup } =
    useQuoteRecipient(value);

  useEffect(() => {
    if (owner && !members?.some((member) => member.id === value.clientId)) {
      onChange({ ...value, clientId: owner.id });
    }
  }, [owner, members, value, onChange]);

  const set =
    (name: keyof Recipient) => (event: ChangeEvent<HTMLInputElement>) =>
      onChange({
        ...value,
        [name]: (event.currentTarget || event.target).value,
      });

  return (
    <>
      <SelectionRow>
        <TextAndImageSelectionField
          title={t('quote.create.recipientOrganization')}
          description={t('quote.create.recipientOrganizationHint')}
          name="recipientKind"
          value="organization"
          isCompact
          checked={value.fromOrganization}
          onChange={() => onChange({ ...value, fromOrganization: true })}
          trackingName="Quote recipient"
        />
        <TextAndImageSelectionField
          title={t('quote.create.recipientProspect')}
          description={t('quote.create.recipientProspectHint')}
          name="recipientKind"
          value="prospect"
          isCompact
          checked={!value.fromOrganization}
          onChange={() => onChange({ ...value, fromOrganization: false })}
          trackingName="Quote recipient"
        />
      </SelectionRow>

      {value.fromOrganization ? (
        <div>
          <Input
            label={t('quote.create.organizationId')}
            requiredStar
            value={value.organizationId}
            onChange={set('organizationId')}
          />
          {lookup === 'found' && (
            <span className={classes.found}>
              <i className="ri-checkbox-circle-line" />
              {organization?.name}
            </span>
          )}
          {lookup === 'empty' && (
            <span className={classes.notFound}>
              {t('quote.create.noOwner')}
            </span>
          )}
          {lookup === 'loading' && (
            <span className={classes.notFound}>
              {t('quote.create.organizationLoading')}
            </span>
          )}
          {lookup === 'invalidId' && (
            <span className={classes.lookupError}>
              <i className="ri-error-warning-line" />
              {t('quote.create.organizationInvalidId')}
            </span>
          )}
          {lookup === 'notFound' && (
            <span className={classes.lookupError}>
              <i className="ri-error-warning-line" />
              {t('quote.create.organizationNotFound')}
            </span>
          )}
          {lookup === 'withoutOwner' && (
            <span className={classes.lookupError}>
              <i className="ri-error-warning-line" />
              {t('quote.create.organizationWithoutOwner', {
                name: organization?.name,
              })}
            </span>
          )}

          {!!owner && (
            <OrganizationMemberSelect
              organizationId={organizationId}
              selectionType="clientId"
              label={t('quote.create.member')}
              value={value.clientId}
              onValueChange={(clientId) =>
                onChange({ ...value, clientId: String(clientId || '') })
              }
            />
          )}
        </div>
      ) : (
        <div className={classes.fields}>
          <Input
            label={t('billing.prospect.firstName')}
            requiredStar
            noMargin
            value={value.firstName}
            onChange={set('firstName')}
          />
          <Input
            label={t('billing.prospect.lastName')}
            requiredStar
            noMargin
            value={value.lastName}
            onChange={set('lastName')}
          />
          <Input
            containerClass={classes.wide}
            label={t('billing.prospect.email')}
            type="email"
            requiredStar
            noMargin
            value={value.email}
            onChange={set('email')}
          />
          <Input
            label={t('billing.prospect.phoneNumber')}
            noMargin
            value={value.phoneNumber}
            onChange={set('phoneNumber')}
          />
          <Input
            label={t('billing.prospect.companyName')}
            requiredStar
            noMargin
            value={value.companyName}
            onChange={set('companyName')}
          />
          <CountryKeySelect
            containerClass={classes.wide}
            label={t('quote.create.prospectCountry')}
            requiredStar
            value={value.billingCountryCode}
            onChange={(option) => {
              onChange({
                ...value,
                billingCountryCode: option
                  ? String((option as OptionsObject).value)
                  : undefined,
              });
            }}
          />
        </div>
      )}
    </>
  );
};
